import { useEffect, useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useSearch } from "@tanstack/react-router";
import { ArrowRight, Check, Sparkles, Star, TrendingUp, Loader2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { PageShell, PageHero } from "@/components/site/page-shell";
import { CTABanner } from "@/components/sections/cta-banner";
import { resolveHostingIcon } from "@/lib/hosting-icons";

type HostingPage = {
  id: string; slug: string; title: string; subtitle: string | null;
  hero_eyebrow: string | null; hero_cta_label: string | null; hero_cta_href: string | null;
  seo_title: string | null; seo_description: string | null; og_image_url: string | null;
  hero_image_url: string | null; hero_image_alt: string | null;
  enabled: boolean; status?: string;
};
type Plan = {
  id: string; name: string; badge: string | null; description: string | null;
  price_monthly_cents: number; price_yearly_cents: number; price_triennial_cents: number;
  currency: string; features: string[]; cta_label: string; cta_href: string;
  is_featured: boolean; enabled: boolean; sort_order: number;
  badge_image_url: string | null; status?: string;
};
type Tile = { id: string; icon: string; title: string; description: string | null; sort_order: number; image_url: string | null; image_alt: string | null; enabled: boolean; status?: string };
type Row = { id: string; label: string; values: Record<string, string>; sort_order: number; highlight_image_url: string | null; enabled: boolean; status?: string };
type Faq = { id: string; question: string; answer: string; sort_order: number; image_url: string | null; enabled: boolean; status?: string };

const CYCLES = [
  { id: "monthly",   label: "Monthly",    months: 1,  key: "price_monthly_cents"   as const },
  { id: "yearly",    label: "12 months",  months: 12, key: "price_yearly_cents"    as const },
  { id: "triennial", label: "36 months",  months: 36, key: "price_triennial_cents" as const },
] as const;
type CycleId = typeof CYCLES[number]["id"];

export function hostingPageQuery(slug: string, preview = false) {
  return {
    queryKey: ["hosting-cms", slug, preview ? "preview" : "live"] as const,
    queryFn: async () => {
      const p = supabase.from("hosting_pages").select("*").eq("slug", slug).maybeSingle();
      // In preview mode, admin RLS allows drafts + disabled rows; skip the `enabled` filter.
      const plans = preview
        ? supabase.from("hosting_plans").select("*").eq("page_slug", slug).order("sort_order")
        : supabase.from("hosting_plans").select("*").eq("page_slug", slug).eq("enabled", true).order("sort_order");
      const tiles = preview
        ? supabase.from("hosting_feature_tiles").select("*").eq("page_slug", slug).order("sort_order")
        : supabase.from("hosting_feature_tiles").select("*").eq("page_slug", slug).eq("enabled", true).order("sort_order");
      const rows = preview
        ? supabase.from("hosting_comparison_rows").select("*").eq("page_slug", slug).order("sort_order")
        : supabase.from("hosting_comparison_rows").select("*").eq("page_slug", slug).order("sort_order");
      const faqs = preview
        ? supabase.from("hosting_faqs").select("*").eq("page_slug", slug).order("sort_order")
        : supabase.from("hosting_faqs").select("*").eq("page_slug", slug).eq("enabled", true).order("sort_order");

      const [pageRes, plansRes, tilesRes, rowsRes, faqsRes] = await Promise.all([p, plans, tiles, rows, faqs]);
      return {
        page: pageRes.data as HostingPage | null,
        plans: (plansRes.data ?? []) as Plan[],
        tiles: (tilesRes.data ?? []) as Tile[],
        rows: (rowsRes.data ?? []) as Row[],
        faqs: (faqsRes.data ?? []) as Faq[],
      };
    },
  };
}

export function HostingSalesPage({
  slug,
  beforeContent,
  afterContent,
}: {
  slug: string;
  beforeContent?: React.ReactNode;
  afterContent?: React.ReactNode;
}) {
  // read preview flag from search params without requiring a specific route
  const search = useSearch({ strict: false }) as { preview?: string };
  const preview = search?.preview === "admin";

  const qc = useQueryClient();
  const { data, isLoading, error } = useQuery(hostingPageQuery(slug, preview));

  // listen for admin CMS refresh pings
  useEffect(() => {
    if (!preview) return;
    const onMsg = (e: MessageEvent) => {
      if (e.origin !== window.location.origin) return;
      if ((e.data as any)?.type === "hosting-cms-refresh") {
        qc.invalidateQueries({ queryKey: ["hosting-cms", slug] });
      }
    };
    window.addEventListener("message", onMsg);
    return () => window.removeEventListener("message", onMsg);
  }, [preview, qc, slug]);

  if (isLoading) {
    return (
      <PageShell>
        <div className="flex min-h-[40vh] items-center justify-center">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      </PageShell>
    );
  }
  if (error || !data?.page) {
    return (
      <PageShell>
        <PageHero eyebrow="Hosting" title="Page not available" sub="This hosting page hasn't been published yet." />
      </PageShell>
    );
  }

  const { page, plans, tiles, rows, faqs } = data;

  return (
    <PageShell>
      {preview && <PreviewBadge status={page.status ?? "published"} />}
      <PageHero
        eyebrow={page.hero_eyebrow ?? "Hosting"}
        title={page.title}
        sub={page.subtitle ?? ""}
      />
      {page.hero_image_url && (
        <div className="mx-auto -mt-6 max-w-5xl px-4 lg:px-8">
          <img src={page.hero_image_url} alt={page.hero_image_alt ?? ""} className="w-full rounded-2xl border border-border object-cover shadow-glow" loading="lazy" />
        </div>
      )}
      {beforeContent}
      {plans.length > 0 && <Pricing plans={plans} preview={preview} />}
      {tiles.length > 0 && <Performance tiles={tiles} preview={preview} />}
      {rows.length > 0 && plans.length > 0 && <Comparison rows={rows} plans={plans} preview={preview} />}
      {faqs.length > 0 && <Faq faqs={faqs} preview={preview} />}
      {afterContent}
      <CTABanner />
    </PageShell>
  );
}


function PreviewBadge({ status }: { status: string }) {
  return (
    <div className="sticky top-0 z-40 border-b border-primary/30 bg-primary/10 px-4 py-2 text-center text-xs font-semibold text-primary">
      Preview mode · showing latest draft{status === "draft" ? " (unpublished changes)" : ""}
    </div>
  );
}

function DraftPill({ enabled, status }: { enabled?: boolean; status?: string }) {
  const isDraft = status === "draft";
  const isOff = enabled === false;
  if (!isDraft && !isOff) return null;
  return (
    <span className="ml-2 inline-flex items-center rounded-full bg-amber-500/15 px-2 py-0.5 text-[10px] font-bold uppercase text-amber-600">
      {isOff ? "Off" : "Draft"}
    </span>
  );
}

function fmt(cents: number, currency: string) {
  return new Intl.NumberFormat("en-US", { style: "currency", currency, minimumFractionDigits: 2 }).format(cents / 100);
}

function Pricing({ plans, preview }: { plans: Plan[]; preview: boolean }) {
  const [cycle, setCycle] = useState<CycleId>("yearly");
  const active = useMemo(() => CYCLES.find((c) => c.id === cycle)!, [cycle]);

  return (
    <section id="plans" className="py-16 sm:py-20">
      <div className="mx-auto max-w-7xl px-4 lg:px-8">
        <div className="text-center max-w-2xl mx-auto">
          <span className="inline-flex items-center gap-2 rounded-full border border-border/60 bg-card/60 px-3 py-1 text-xs font-semibold text-primary">
            <Sparkles className="h-3.5 w-3.5" /> Hosting packages
          </span>
          <h2 className="mt-4 text-3xl sm:text-5xl font-bold tracking-tight">
            Pick a plan. <span className="bg-gradient-to-r from-primary to-accent bg-clip-text text-transparent">Scale anytime.</span>
          </h2>
          <p className="mt-4 text-muted-foreground">Transparent pricing. No hidden fees. Free migration and 30-day money-back.</p>
        </div>

        <div className="mt-8 flex justify-center">
          <div role="tablist" aria-label="Billing cycle" className="inline-flex rounded-full border border-border bg-card/60 p-1 shadow-sm backdrop-blur">
            {CYCLES.map((c) => {
              const isActive = c.id === cycle;
              const anyMonthly = plans[0]?.price_monthly_cents ?? 0;
              const cyc = plans[0]?.[c.key] ?? 0;
              const discount = anyMonthly ? Math.max(0, Math.round(((anyMonthly - cyc) / anyMonthly) * 100)) : 0;
              return (
                <button
                  key={c.id} role="tab" aria-selected={isActive}
                  onClick={() => setCycle(c.id)}
                  className={`relative rounded-full px-4 sm:px-5 py-2 text-xs sm:text-sm font-semibold transition ${
                    isActive ? "bg-gradient-to-r from-primary to-accent text-white shadow-glow" : "text-muted-foreground hover:text-foreground"
                  }`}
                >
                  {c.label}
                  {c.id !== "monthly" && discount > 0 && (
                    <span className={`ml-2 hidden sm:inline-flex items-center rounded-full px-1.5 py-0.5 text-[10px] font-bold ${isActive ? "bg-white/20 text-white" : "bg-primary/10 text-primary"}`}>
                      -{discount}%
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        </div>

        <div className="mt-10 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {plans.map((p) => {
            const priceCents = p[active.key];
            const monthly = p.price_monthly_cents;
            const save = cycle === "monthly" || monthly === 0 ? 0 : Math.round(((monthly - priceCents) / monthly) * 100);
            return (
              <article key={p.id} className={`group relative flex flex-col rounded-2xl border p-6 sm:p-7 transition ${
                p.is_featured ? "border-primary/60 bg-gradient-to-b from-primary/5 to-transparent shadow-glow" : "border-border bg-card hover:border-primary/40 hover:-translate-y-0.5"
              }`}>
                {(p.is_featured || p.badge) && (
                  <span className="absolute -top-3 left-1/2 -translate-x-1/2 inline-flex items-center gap-1 rounded-full bg-gradient-to-r from-primary to-accent px-3 py-1 text-[11px] font-bold uppercase tracking-wider text-white shadow-glow">
                    {p.badge_image_url ? <img src={p.badge_image_url} alt="" className="h-3 w-3 rounded-full object-cover" /> : <Star className="h-3 w-3" />} {p.badge ?? "Most popular"}
                  </span>
                )}
                <header>
                  <h3 className="text-xl font-bold">{p.name}{preview && <DraftPill enabled={p.enabled} status={p.status} />}</h3>
                  {p.description && <p className="mt-1 text-sm text-muted-foreground">{p.description}</p>}
                </header>
                <div className="mt-5 flex items-end gap-2">
                  <span className="text-4xl sm:text-5xl font-black tracking-tight">{fmt(priceCents, p.currency)}</span>
                  <span className="mb-1.5 text-sm text-muted-foreground">/ mo</span>
                </div>
                <div className="mt-1 flex flex-wrap items-center gap-2 text-xs">
                  {save > 0 ? (
                    <>
                      <span className="text-muted-foreground line-through">{fmt(monthly, p.currency)}/mo</span>
                      <span className="rounded-full bg-emerald-500/15 px-2 py-0.5 font-bold text-emerald-500">Save {save}%</span>
                    </>
                  ) : <span className="text-muted-foreground">Billed monthly</span>}
                  <span className="text-muted-foreground">· {fmt(priceCents * active.months, p.currency)} billed {active.months === 1 ? "monthly" : `every ${active.months} mo`}</span>
                </div>
                <ul className="mt-6 space-y-2.5 text-sm">
                  {(Array.isArray(p.features) ? p.features : []).map((b, i) => (
                    <li key={i} className="flex items-start gap-2">
                      <span className="mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded-full bg-primary/15 text-primary">
                        <Check className="h-3 w-3" strokeWidth={3} />
                      </span>
                      <span className="text-muted-foreground">{b}</span>
                    </li>
                  ))}
                </ul>
                <div className="mt-7 pt-2">
                  <a
                    href={p.cta_href || "#"}
                    className={`inline-flex w-full items-center justify-center gap-2 rounded-full px-5 py-3 text-sm font-semibold transition ${
                      p.is_featured ? "bg-gradient-to-r from-primary to-accent text-white shadow-glow hover:opacity-95" : "bg-foreground text-background hover:opacity-90"
                    }`}
                  >
                    {p.cta_label || "Get started"} <ArrowRight className="h-4 w-4" />
                  </a>
                </div>
              </article>
            );
          })}
        </div>
      </div>
    </section>
  );
}

function Performance({ tiles, preview }: { tiles: Tile[]; preview: boolean }) {
  return (
    <section className="py-16 sm:py-20">
      <div className="mx-auto max-w-7xl px-4 lg:px-8">
        <div className="text-center max-w-2xl mx-auto">
          <span className="inline-flex items-center gap-2 rounded-full border border-border/60 bg-card/60 px-3 py-1 text-xs font-semibold text-primary">
            <TrendingUp className="h-3.5 w-3.5" /> Built for performance
          </span>
          <h2 className="mt-4 text-3xl sm:text-4xl font-bold tracking-tight">Every plan ships with speed & security baked in</h2>
        </div>
        <div className="mt-10 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {tiles.map((f) => {
            const Icon = resolveHostingIcon(f.icon);
            return (
              <div key={f.id} className="group relative overflow-hidden rounded-2xl border border-border bg-card p-5 transition hover:-translate-y-0.5 hover:border-primary/40 hover:shadow-glow">
                {f.image_url ? (
                  <img src={f.image_url} alt={f.image_alt ?? ""} className="mb-3 h-16 w-16 rounded-xl object-cover" loading="lazy" />
                ) : (
                  <span className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-gradient-to-br from-primary/15 to-accent/15 text-primary transition group-hover:from-primary group-hover:to-accent group-hover:text-white">
                    <Icon className="h-5 w-5" />
                  </span>
                )}
                <h3 className="mt-4 text-base font-semibold">{f.title}{preview && <DraftPill enabled={f.enabled} status={f.status} />}</h3>
                {f.description && <p className="mt-1 text-sm text-muted-foreground">{f.description}</p>}
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

function Comparison({ rows, plans, preview }: { rows: Row[]; plans: Plan[]; preview: boolean }) {
  return (
    <section className="py-16 sm:py-20">
      <div className="mx-auto max-w-7xl px-4 lg:px-8">
        <div className="text-center max-w-2xl mx-auto">
          <h2 className="text-3xl sm:text-4xl font-bold tracking-tight">Compare every plan</h2>
          <p className="mt-3 text-muted-foreground">Side-by-side specs so you can pick with confidence.</p>
        </div>
        <div className="mt-8 overflow-x-auto rounded-2xl border border-border bg-card">
          <table className="w-full min-w-[640px] text-sm">
            <thead>
              <tr className="border-b border-border bg-muted/40">
                <th className="p-4 text-left text-xs font-semibold uppercase tracking-wider text-muted-foreground">Feature</th>
                {plans.map((p) => (
                  <th key={p.id} className="p-4 text-left text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                    {p.name}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} className="border-b border-border/60 last:border-0">
                  <td className="p-4 font-medium">
                    <div className="flex items-center gap-2">
                      {r.highlight_image_url && <img src={r.highlight_image_url} alt="" className="h-6 w-6 rounded object-cover" />}
                      <span>{r.label}{preview && <DraftPill enabled={r.enabled} status={r.status} />}</span>
                    </div>
                  </td>
                  {plans.map((p) => (
                    <td key={p.id} className="p-4 text-muted-foreground">{r.values?.[p.name] ?? "—"}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </section>
  );
}

function Faq({ faqs, preview }: { faqs: Faq[]; preview: boolean }) {
  return (
    <section className="py-16 sm:py-20">
      <div className="mx-auto max-w-4xl px-4 lg:px-8">
        <h2 className="text-center text-3xl sm:text-4xl font-bold tracking-tight">Frequently asked questions</h2>
        <div className="mt-8 divide-y divide-border rounded-2xl border border-border bg-card">
          {faqs.map((f) => (
            <details key={f.id} className="group p-5">
              <summary className="flex cursor-pointer list-none items-center justify-between gap-4 font-semibold">
                <span className="flex items-center gap-3">
                  {f.image_url && <img src={f.image_url} alt="" className="h-8 w-8 rounded-lg object-cover" />}
                  <span>{f.question}{preview && <DraftPill enabled={f.enabled} status={f.status} />}</span>
                </span>
                <span className="text-primary transition group-open:rotate-180">▾</span>
              </summary>
              <p className="mt-3 text-sm text-muted-foreground">{f.answer}</p>
            </details>
          ))}
        </div>
      </div>
    </section>
  );
}

export type { HostingPage as HostingPageRow };
